/**
 * STM32 Embedded C Code Suite – main page (redesigned)
 * Full folder tree rendered on load with checkboxes.
 * MCU search input is a convenience filter to pick your STM32 family.
 * FreeRTOS toggle is prominent.
 * Renders under Open Source → Libraries tab.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react'; // React core
import { useTranslation } from 'react-i18next'; // i18n translations
import axios from 'axios'; // HTTP client for API calls
import FolderTree from '../../../../components/openSource/libraries/stm32/FolderTree'; // Tree with checkboxes
import DownloadButton from '../../../../components/openSource/libraries/stm32/DownloadButton'; // Download zip button
import Skeleton from '../../../../components/openSource/libraries/stm32/Skeleton'; // Loading skeletons

const API_BASE = '/api/open-source/stm32'; // Base URL for STM32 API

export default function STM32SuitePage() {
  const { t } = useTranslation(); // Translation hook
  const [allMcus, setAllMcus] = useState([]); // Full MCU list from backend (loaded once)
  const [tree, setTree] = useState(null); // Folder tree definition from backend
  const [selectedMcu, setSelectedMcu] = useState(null); // Currently selected MCU object
  const [selectedNodeIds, setSelectedNodeIds] = useState(new Set()); // Checked node IDs in tree
  const [includeFreeRTOS, setIncludeFreeRTOS] = useState(false); // FreeRTOS toggle
  const [loadingMcus, setLoadingMcus] = useState(true); // Loading state for MCU list
  const [loadingTree, setLoadingTree] = useState(true); // Loading state for tree
  const [searchQuery, setSearchQuery] = useState(''); // MCU search filter text
  const [mcuDropdownOpen, setMcuDropdownOpen] = useState(false); // Whether MCU dropdown is visible

  // Debug: log render with timestamp (helps catch unnecessary re-renders)
  useEffect(() => {
    console.log(`[${new Date().toISOString()}] [STM32SuitePage] render`);
  });

  // Load folder tree once on mount
  useEffect(() => {
    let cancelled = false; // Prevent stale state updates if unmounted
    console.log(`[${new Date().toISOString()}] [STM32SuitePage] fetch tree start`);
    setLoadingTree(true); // Show skeleton while loading
    axios
      .get(`${API_BASE}/tree`) // GET folder tree JSON
      .then((res) => {
        if (!cancelled) {
          console.log(`[${new Date().toISOString()}] [STM32SuitePage] fetch tree done`, { rootId: res.data?.rootId, nodeCount: Object.keys(res.data?.nodes || {}).length });
          setTree(res.data); // Store tree data
        }
      })
      .catch((err) => {
        if (!cancelled) console.error(`[${new Date().toISOString()}] [STM32SuitePage] fetch tree error`, err);
      })
      .finally(() => {
        if (!cancelled) setLoadingTree(false); // Hide skeleton
      });
    return () => { cancelled = true; }; // Cleanup on unmount
  }, []);

  // Load full MCU list once on mount (no search query – load everything)
  useEffect(() => {
    let cancelled = false; // Prevent stale state updates
    console.log(`[${new Date().toISOString()}] [STM32SuitePage] fetch all MCUs start`);
    setLoadingMcus(true); // Show loading state
    axios
      .get(`${API_BASE}/mcus`) // GET all MCUs (no query param = all)
      .then((res) => {
        if (!cancelled) {
          // Backend returns { mcus: [...] }
          const mcuArray = res.data?.mcus || res.data || []; // Handle both shapes
          console.log(`[${new Date().toISOString()}] [STM32SuitePage] fetch MCUs done`, { count: mcuArray.length, firstId: mcuArray[0]?.id });
          setAllMcus(Array.isArray(mcuArray) ? mcuArray : []); // Store full list
        }
      })
      .catch((err) => {
        if (!cancelled) console.error(`[${new Date().toISOString()}] [STM32SuitePage] fetch MCUs error`, err);
        setAllMcus([]); // Fallback to empty
      })
      .finally(() => {
        if (!cancelled) setLoadingMcus(false); // Hide loading
      });
    return () => { cancelled = true; }; // Cleanup
  }, []);

  // Filter MCUs client-side based on search query (no extra API calls)
  const filteredMcus = useMemo(() => {
    const q = searchQuery.trim().toLowerCase(); // Normalize query
    if (!q) return allMcus; // No filter = show all
    return allMcus.filter((m) => {
      // Match against name, family, or any search term
      const nameMatch = (m.name || '').toLowerCase().includes(q);
      const familyMatch = (m.family || '').toLowerCase().includes(q);
      const termsMatch = (m.searchTerms || []).some((term) => String(term).toLowerCase().includes(q));
      return nameMatch || familyMatch || termsMatch;
    });
  }, [searchQuery, allMcus]);

  // Group filtered MCUs by family for a nicer dropdown
  const groupedMcus = useMemo(() => {
    const groups = {}; // { family: [mcu, mcu, ...] }
    filteredMcus.forEach((m) => {
      const fam = m.family || 'Other'; // Group key
      if (!groups[fam]) groups[fam] = []; // Init array
      groups[fam].push(m); // Add MCU to group
    });
    // Sort families alphabetically
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredMcus]);

  // Handle MCU selection from dropdown
  const handleSelectMcu = useCallback((mcu) => {
    console.log(`[${new Date().toISOString()}] [STM32SuitePage] MCU selected`, { id: mcu.id, name: mcu.name, core: mcu.core });
    setSelectedMcu(mcu); // Store selected MCU
    setSearchQuery(mcu.name); // Show selected name in input
    setMcuDropdownOpen(false); // Close dropdown
  }, []);

  // Handle FreeRTOS toggle – sync with selectedNodeIds
  const handleFreeRTOSToggle = useCallback(() => {
    setIncludeFreeRTOS((prev) => {
      const next = !prev; // Toggle
      console.log(`[${new Date().toISOString()}] [STM32SuitePage] FreeRTOS toggle`, { include: next });
      // Sync rtos_freertos in selectedNodeIds
      setSelectedNodeIds((prevIds) => {
        const newIds = new Set(prevIds); // Clone set
        if (next) {
          newIds.add('rtos_freertos'); // Add FreeRTOS node
        } else {
          newIds.delete('rtos_freertos'); // Remove FreeRTOS node
        }
        return newIds;
      });
      return next;
    });
  }, []);

  // Keep FreeRTOS toggle in sync if user checks/unchecks it in the tree
  useEffect(() => {
    const hasFreeRTOS = selectedNodeIds.has('rtos_freertos'); // Check if FreeRTOS is in set
    if (hasFreeRTOS !== includeFreeRTOS) {
      setIncludeFreeRTOS(hasFreeRTOS); // Sync toggle state
    }
  }, [selectedNodeIds, includeFreeRTOS]);

  // Tailwind color classes for consistent theming
  const primaryText = 'text-silver-light'; // Main headings
  const secondaryText = 'text-silver-medium'; // Descriptions
  const cardBg = 'bg-gray-900 border border-gray-700 rounded-xl'; // Card background

  return (
    <div className="max-w-5xl mx-auto px-4 pb-12">
      {/* Page title and description */}
      <div className="mb-8">
        <h2 className={`text-2xl md:text-3xl font-bold mb-3 ${primaryText}`}>
          {t('openSource.libraries.stm32.title', 'STM32 Embedded C Code Suite')}
        </h2>
        <p className={`${secondaryText} mb-2 text-base`}>
          {t('openSource.libraries.stm32.description', 'Select your MCU, pick the drivers and peripherals you need, and download a ready-to-import project folder.')}
        </p>
      </div>

      {/* === MCU SELECTOR === */}
      <section className="mb-8">
        <h3 className={`text-lg font-semibold mb-3 ${primaryText} flex items-center gap-2`}>
          {/* Chip icon */}
          <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 3v2m6-2v2M9 19v2m6-2v2M3 9h2m-2 6h2m14-6h2m-2 6h2M7 7h10a2 2 0 012 2v6a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2z" />
          </svg>
          {t('openSource.libraries.stm32.step1', '1. Select your MCU')}
        </h3>

        <div className="relative">
          {/* Search input */}
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value); // Update filter text
              setMcuDropdownOpen(true); // Open dropdown on typing
              if (selectedMcu && e.target.value !== selectedMcu.name) {
                setSelectedMcu(null); // Clear selection if user edits text
              }
            }}
            onFocus={() => setMcuDropdownOpen(true)} // Open dropdown on focus
            placeholder={t('openSource.libraries.stm32.searchPlaceholder', 'Search MCU... e.g. STM32F4, F103, G0, blue pill')}
            className="w-full px-4 py-3 rounded-xl bg-gray-900 border border-gray-700 text-silver-light placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-colors"
          />
          {/* Loading indicator */}
          {loadingMcus && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="w-5 h-5 border-2 border-gray-600 border-t-blue-400 rounded-full animate-spin" />
            </div>
          )}

          {/* Selected MCU badge */}
          {selectedMcu && (
            <div className="mt-2 flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-blue-900/30 border border-blue-700/50 text-blue-300 text-sm font-medium">
                {/* Check icon */}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                {selectedMcu.name}
                <span className="text-blue-400/70 ml-1">({selectedMcu.family}, {selectedMcu.core})</span>
              </span>
              {/* Clear button */}
              <button
                type="button"
                onClick={() => {
                  setSelectedMcu(null); // Clear MCU
                  setSearchQuery(''); // Clear search
                }}
                className="text-gray-500 hover:text-gray-300 text-xs transition-colors"
                title="Clear selection"
              >
                Clear
              </button>
            </div>
          )}

          {/* MCU dropdown list */}
          {mcuDropdownOpen && !loadingMcus && (
            <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto rounded-xl border border-gray-700 bg-gray-900 shadow-2xl">
              {groupedMcus.length === 0 ? (
                <div className="px-4 py-3 text-gray-400 text-sm">
                  No MCUs found. Try searching for STM32F4, F103, G0, etc.
                </div>
              ) : (
                groupedMcus.map(([family, mcus]) => (
                  <div key={family}>
                    {/* Family group header */}
                    <div className="px-4 py-1.5 bg-gray-800/60 text-xs font-semibold text-gray-400 uppercase tracking-wide sticky top-0">
                      {family} Series
                    </div>
                    {mcus.map((mcu) => (
                      <button
                        key={mcu.id}
                        type="button"
                        className={`w-full text-left px-4 py-2 hover:bg-gray-800 transition-colors flex items-center justify-between ${
                          selectedMcu?.id === mcu.id ? 'bg-blue-900/20 text-blue-300' : 'text-silver-light'
                        }`}
                        onClick={() => handleSelectMcu(mcu)} // Select this MCU
                      >
                        <span className="font-medium">{mcu.name}</span>
                        <span className="text-gray-500 text-sm">{mcu.core}</span>
                      </button>
                    ))}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Click-away handler to close dropdown */}
        {mcuDropdownOpen && (
          <div
            className="fixed inset-0 z-10"
            onClick={() => setMcuDropdownOpen(false)} // Close on outside click
          />
        )}
      </section>

      {/* === FreeRTOS TOGGLE (prominent) === */}
      <section className="mb-8">
        <div
          className={`${cardBg} p-4 flex items-center justify-between cursor-pointer hover:border-green-700/50 transition-colors ${
            includeFreeRTOS ? 'border-green-700/70 bg-green-900/10' : ''
          }`}
          onClick={handleFreeRTOSToggle} // Toggle on click anywhere in card
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleFreeRTOSToggle(); }}
        >
          <div className="flex items-center gap-3">
            {/* RTOS icon */}
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${includeFreeRTOS ? 'bg-green-800/40' : 'bg-gray-800'}`}>
              <svg className={`w-5 h-5 ${includeFreeRTOS ? 'text-green-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <div>
              <h4 className={`font-semibold ${includeFreeRTOS ? 'text-green-300' : primaryText}`}>
                Include FreeRTOS
              </h4>
              <p className="text-sm text-gray-400">
                Kernel + correct GCC port for your MCU. Bootstrap in minutes — saves days of integration work.
              </p>
            </div>
          </div>
          {/* Toggle switch */}
          <div className={`relative w-12 h-6 rounded-full transition-colors ${includeFreeRTOS ? 'bg-green-600' : 'bg-gray-700'}`}>
            <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${includeFreeRTOS ? 'translate-x-6' : 'translate-x-0.5'}`} />
          </div>
        </div>
      </section>

      {/* === FOLDER TREE (always rendered) === */}
      <section className="mb-8">
        <h3 className={`text-lg font-semibold mb-3 ${primaryText} flex items-center gap-2`}>
          {/* Folder icon */}
          <svg className="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          {t('openSource.libraries.stm32.step2', '2. Select drivers & peripherals')}
        </h3>
        {loadingTree ? (
          /* Loading skeleton while tree loads */
          <div className={`${cardBg} p-5`}>
            <Skeleton variant="tree" />
            <p className={`mt-3 text-sm ${secondaryText}`}>
              {t('openSource.libraries.stm32.loadingTree', 'Loading folder tree…')}
            </p>
          </div>
        ) : tree ? (
          /* Render the full tree with checkboxes */
          <FolderTree
            tree={tree}
            selectedNodeIds={selectedNodeIds}
            onSelectionChange={setSelectedNodeIds}
          />
        ) : (
          /* Error fallback */
          <p className={secondaryText}>{t('openSource.libraries.stm32.treeError', 'Could not load folder tree.')}</p>
        )}
      </section>

      {/* === DOWNLOAD === */}
      <section>
        <h3 className={`text-lg font-semibold mb-3 ${primaryText} flex items-center gap-2`}>
          {/* Download icon */}
          <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          {t('openSource.libraries.stm32.step3', '3. Download')}
        </h3>
        {/* Summary of what will be downloaded */}
        {selectedMcu && selectedNodeIds.size > 0 && (
          <div className="mb-3 text-sm text-gray-400">
            <span className="text-silver-light font-medium">{selectedMcu.name}</span>
            {' '}&middot;{' '}
            {selectedNodeIds.size} module{selectedNodeIds.size !== 1 ? 's' : ''} selected
            {includeFreeRTOS && <span className="text-green-400 ml-1">+ FreeRTOS</span>}
          </div>
        )}
        <DownloadButton
          mcuId={selectedMcu?.id}
          nodeIds={Array.from(selectedNodeIds)}
          disabled={!selectedMcu || selectedNodeIds.size === 0}
        />
        {/* Hint if no MCU selected */}
        {!selectedMcu && (
          <p className="mt-2 text-sm text-gray-500">
            Select an MCU above to enable download.
          </p>
        )}
      </section>
    </div>
  );
}
